import { isObjEqual } from "./compare";
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// defers a function call until the next render so it has access to the latest state
export const useDeferredCall = <T extends unknown[]>(
  func: (...args: T) => void
) => {
  const [queue, setQueue] = useState<T[]>([]);

  const dispatch = useCallback((...args: T) => {
    setQueue((prevQueue) => [...prevQueue, args]);
  }, []);

  useEffect(() => {
    if (queue.length > 0) {
      const args = queue[0];
      setQueue((prevQueue) => prevQueue.slice(1));
      func(...args);
    }
  }, [queue]);

  return dispatch;
};

export const useMemoObject = <T extends Record<string, unknown>>(obj: T): T => {
  return useMemo(() => obj, Object.values(obj));
};

export const useStateWithRef = <T>(
  initialValue: T
): [T, Dispatch<SetStateAction<T>>, React.MutableRefObject<T>] => {
  const [state, setState] = useState<T>(initialValue);
  const stateRef = useRef<T>(initialValue);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  return [state, setState, stateRef];
};

export const useCancelableTimeout = () => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const cancelTimeout = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
  }, []);

  const scheduleTimeout = useCallback(
    (callback: () => void, delayMs: number) => {
      cancelTimeout();
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = undefined;
        callback();
      }, delayMs);
    },
    [cancelTimeout]
  );

  useEffect(() => cancelTimeout, [cancelTimeout]);

  return { cancelTimeout, scheduleTimeout };
};

export const useEffectWithPrevVals = <T extends unknown[]>(
  func: (prevDeps: T) => void,
  deps: T
) => {
  const prevDeps = useRef(deps);
  useEffect(() => {
    func(prevDeps.current);
    prevDeps.current = deps;
  }, deps);
};

export const useEffectOnlyOnChange = <T extends unknown[]>(
  func: () => void,
  deps: T
) => {
  useEffectWithPrevVals((prevDeps) => {
    if (prevDeps.every((prev, i) => prev === deps[i])) return;
    func();
  }, deps);
};

export const useEffectOnlyOnce = (func: () => void) => {
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    func();
  }, []);
};

/** only update if the deps change with !isObjEqual(oldDeps, newDeps) */
export const useDeepMemo = <T>(func: () => T, deps: unknown[]): T => {
  const [value, setValue] = useState<T>(() => func());
  useEffectWithPrevVals((prevDeps) => {
    if (prevDeps.every((prev, i) => isObjEqual(prev, deps[i]))) return;
    setValue(() => func());
  }, deps);
  return value;
};

/** A ref that updates when the dependencies change */
export const useUpdatingRef = <T, D extends unknown[]>(
  func: (...args: D) => T,
  deps: D
): React.MutableRefObject<T> => {
  const ref = useRef<T>(func(...deps));
  useEffect(() => {
    ref.current = func(...deps);
  }, deps);
  return ref;
};

/**
 * Normal useState that returns two copies of the state variable. One normal state A and
 * one state B which is updated only if there are no changes in A for *debounceDelay* milliseconds.
 * Use the latter for time consuming function calls.
 */
export const useStateWithDebounce = <T>(
  initialState: T | (() => T),
  debounceDelay: number
): [T, Dispatch<SetStateAction<T>>, T] => {
  const [currentValue, setCurrentValue] = useState<T>(initialState);
  const [debounceValue, setDebounceValue] = useState(() => currentValue);
  useEffect(() => {
    const timer = setTimeout(
      () => setDebounceValue(() => currentValue),
      debounceDelay
    );
    return () => clearTimeout(timer);
  }, [currentValue, debounceDelay]);
  return [currentValue, setCurrentValue, debounceValue];
};

// It keeps a local state copy that is updated when the global state changes.
// After debounceDelay milliseconds, the local state is updated to the global state.
export const useLocalDebounceStateCopy = <T>(
  globalState: T,
  setGlobalState: (value: T) => void,
  debounceDelay: number
) => {
  const [currentValue, setCurrentValue] = useState<T>(globalState);
  // Used to avoid syncing the global state back to the local state if it hasn't changed.
  // Unnecessary syncing causes the cursor to jump to the end of the text area when typing in the question box.
  const lastSentValue = useRef<T>(globalState);

  useEffect(() => {
    if (currentValue === globalState) return;
    const timer = setTimeout(() => {
      lastSentValue.current = currentValue;
      setGlobalState(currentValue);
    }, debounceDelay);
    return () => clearTimeout(timer);
  }, [currentValue, debounceDelay, setGlobalState]);

  useEffect(() => {
    if (globalState !== lastSentValue.current && globalState !== currentValue) {
      setCurrentValue(globalState);
      lastSentValue.current = globalState;
    }
  }, [globalState, currentValue]);

  return [currentValue, setCurrentValue] as const;
};

export const useEffectDebounced = (
  callback: () => void,
  deps: unknown[],
  delay: number
) => {
  useEffect(() => {
    const handler = setTimeout(() => {
      callback();
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [...deps, delay]);
};

/**
 * Debounces a boolean's transition to `true` by `delayMs`.
 * Transitions to `false` are applied immediately.
 * Useful for avoiding loading-indicator flicker on fast navigations.
 */
export const useDebouncedTrue = (value: boolean, delayMs: number) => {
  const [debounced, setDebounced] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (value) {
      timerRef.current = setTimeout(() => setDebounced(true), delayMs);
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
      setDebounced(false);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, delayMs]);

  return debounced;
};

/** Starts at `value`, so only later changes wait `delayMs` to settle. */
export const useDebouncedValue = <T>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

export const useInView = ({
  element_ref,
  root,
  rootMargin,
  threshold,
  track_current_visibility = false,
}: {
  element_ref: RefObject<Element | null>;
  root?: Document | Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  track_current_visibility?: boolean;
}) => {
  const [has_been_visible, set_has_been_visible] = useState(false);
  const [is_in_view, set_is_in_view] = useState(false);

  useEffect(() => {
    const element = element_ref.current;
    if (!element) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting || entry.intersectionRatio > 0;
        if (track_current_visibility) {
          set_is_in_view(visible);
          if (visible) {
            set_has_been_visible(true);
          }
        } else if (visible) {
          set_has_been_visible(true);
          observer.unobserve(element);
        }
      },
      { root, threshold, rootMargin }
    );
    observer.observe(element);
    return () => {
      observer.unobserve(element);
      observer.disconnect();
    };
  }, []);

  return { has_been_visible, is_in_view };
};

export type PollOptions = {
  intervalMs: number;
  timeoutMs: number;
};

export async function pollUntil<T>(
  fn: () => Promise<T | null>,
  condition: (result: T | null) => boolean,
  options: PollOptions,
  signal?: AbortSignal
): Promise<T | null> {
  const { intervalMs, timeoutMs } = options;
  const startTime = Date.now();

  while (!signal?.aborted) {
    const result = await fn();

    if (condition(result)) {
      return result;
    }

    if (Date.now() - startTime > timeoutMs) {
      throw new Error("Poll timeout");
    }

    // Wait for interval, but abort-aware
    await new Promise<void>((resolve) => {
      const abortHandler = () => {
        clearTimeout(timeoutId);
        resolve();
      };

      const timeoutId = setTimeout(() => {
        signal?.removeEventListener("abort", abortHandler);
        resolve();
      }, intervalMs);

      signal?.addEventListener("abort", abortHandler, { once: true });
    });
  }

  return null;
}

export function usePollUntil() {
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const poll = useCallback(
    <T>(
      fn: () => Promise<T | null>,
      condition: (result: T | null) => boolean,
      options: PollOptions
    ): Promise<T | null> => {
      // Abort any existing poll to prevent orphaned loops
      controllerRef.current?.abort();
      controllerRef.current = new AbortController();
      return pollUntil(fn, condition, options, controllerRef.current.signal);
    },
    []
  );

  const cancel = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  return { poll, cancel };
}

export const useScrollShadows = () => {
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const scrollElementRef = useRef<HTMLElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const updateScrollShadows = useCallback(() => {
    const el = scrollElementRef.current;
    if (!el) return;
    setShowTopShadow(el.scrollTop > 0);
    setShowBottomShadow(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  const scrollShadowRef = useCallback(
    (el: HTMLElement | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      scrollElementRef.current = el;

      if (!el) return;

      updateScrollShadows();
      el.addEventListener("scroll", updateScrollShadows, { passive: true });
      const observer = new ResizeObserver(updateScrollShadows);
      observer.observe(el);
      // The scroller's own box does not change when its content grows (e.g.
      // images finishing loading), so observe the children too, matching
      // useHorizontalScrollShadows.
      Array.from(el.children).forEach((child) => observer.observe(child));

      cleanupRef.current = () => {
        el.removeEventListener("scroll", updateScrollShadows);
        observer.disconnect();
      };
    },
    [updateScrollShadows]
  );

  useEffect(() => {
    return () => cleanupRef.current?.();
  }, []);

  return {
    showTopShadow,
    showBottomShadow,
    scrollShadowRef,
    updateScrollShadows,
  };
};

export const useHorizontalScrollShadows = (observeDeps: unknown[] = []) => {
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const scrollElementRef = useRef<HTMLElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const updateScrollShadows = useCallback(() => {
    const el = scrollElementRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setShowLeftShadow(el.scrollLeft > 1);
    setShowRightShadow(el.scrollLeft < maxScrollLeft - 1);
  }, []);

  const observeScrollContent = useCallback(() => {
    const el = scrollElementRef.current;
    const observer = resizeObserverRef.current;
    if (!el || !observer) return;
    observer.disconnect();
    observer.observe(el);
    Array.from(el.children).forEach((child) => {
      if (child instanceof HTMLElement) observer.observe(child);
    });
  }, []);

  const scrollShadowRef = useCallback(
    (el: HTMLElement | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      scrollElementRef.current = el;

      if (!el) return;

      updateScrollShadows();
      el.addEventListener("scroll", updateScrollShadows, { passive: true });

      if (typeof ResizeObserver !== "undefined") {
        resizeObserverRef.current = new ResizeObserver(updateScrollShadows);
        observeScrollContent();
      }

      cleanupRef.current = () => {
        el.removeEventListener("scroll", updateScrollShadows);
        resizeObserverRef.current?.disconnect();
        resizeObserverRef.current = null;
      };
    },
    [observeScrollContent, updateScrollShadows]
  );

  useEffect(() => {
    observeScrollContent();
    updateScrollShadows();
  }, [observeScrollContent, updateScrollShadows, ...observeDeps]);

  useEffect(() => {
    return () => cleanupRef.current?.();
  }, []);

  return {
    showLeftShadow,
    showRightShadow,
    scrollShadowRef,
    scrollElementRef,
    updateScrollShadows,
  };
};

export const usePositionIndependentOfScroll = ({
  position: initial_position,
  container = window,
}: {
  position: { x: number; y: number };
  container?: Element | Window | null;
}) => {
  const [position, set_position] = useState(initial_position);
  if (!container) {
    container = window;
  }
  const last_scroll_position = useRef({
    x:
      container === window
        ? container.scrollX
        : (container as Element).scrollLeft,
    y:
      container === window
        ? container.scrollY
        : (container as Element).scrollTop,
  });

  useEffect(() => {
    const handle_scroll = () => {
      const current_x =
        container === window
          ? container.scrollX
          : (container as Element).scrollLeft;
      const current_y =
        container === window
          ? container.scrollY
          : (container as Element).scrollTop;

      set_position((p) => ({
        x: p.x + last_scroll_position.current.x - current_x,
        y: p.y + last_scroll_position.current.y - current_y,
      }));

      last_scroll_position.current = {
        x: current_x,
        y: current_y,
      };
    };

    container.addEventListener("scroll", handle_scroll);
    return () => {
      container.removeEventListener("scroll", handle_scroll);
    };
  }, [container]);

  return position;
};
